import React, { forwardRef } from "react";

import { UserType } from "@hubcapp/types";

import { ArrowRight } from "react-feather";
import {
  Box,
  Paper,
  TablePagination,
  colors,
  fade,
  makeStyles,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useTheme } from "@material-ui/core/styles";

import {
  AddBox,
  ArrowUpward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";

import MaterialTable, {
  MTableCell as Cell,
  Icons,
  MTableBodyRow as Row,
} from "material-table";

import { formatDistance } from "@ds-developer/frontend";
import Label from "@ds-developer/label";

import { lessonTypeToDisplayString } from "@hubcapp/utils";
import { useTealium } from "@hubcapp/context";

import { LearnersTableItem, LearnersTableProps } from "./types";
import { LearnersTableToolbar } from "./components";

// We almost certainly don't need all these icons but I don't have the time nor energy to work out which right now.
// Feel free to refine the list...
const tableIcons: Icons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const useStyles = makeStyles(theme => ({
  root: {
    margin: 0,
  },
  cell: {
    [theme.breakpoints.up("md")]: {
      height: theme.spacing(9),
    },
  },
  pagination: {
    minHeight: theme.spacing(7),
  },
  sameLine: {
    display: "flex",
    flexDirection: "row",
    marginRight: theme.spacing(2),
  },
  ss: {
    color: colors.grey["500"],
    backgroundColor: fade(colors.grey["500"], 0.08),
  },
  labelSpacing: {
    flexFlow: "row nowrap",
    display: "flex",
    gap: "0.5rem",
  },
}));

const LearnersTable: React.FC<LearnersTableProps> = ({
  loading = false,
  learners = [],
  toggleActiveFilter = () => {},
  activeFilter = true,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { trackEvent } = useTealium();

  const onRowClick = (
    _event: any,
    data?: LearnersTableItem | LearnersTableItem[],
  ) => {
    if (!data) return null;
    trackEvent({
      eventCategory: "Learner",
      eventAction: "Interaction",
      eventLabel: "View Learner",
    });
    if (!Array.isArray(data)) {
      history.push(`/learners/${data.id}`);
    }
  };

  return (
    <Box m={1} className={`${classes.root} mobile-friendly has-actions`}>
      <MaterialTable
        icons={tableIcons}
        isLoading={loading}
        columns={[
          {
            title: "Name",
            field: "name",
            sorting: true,
            searchable: true,
            render: rowData => (
              <div className={classes.sameLine}>
                <div style={{ marginRight: "0.5rem" }} data-label="Name">
                  {rowData.name}
                </div>
                <div className={classes.labelSpacing} data-label="Labels">
                  {!rowData.approved && <Label color="success">New</Label>}
                  {rowData.sourcedBy === UserType.Instructor && (
                    <Label color="ss">SS</Label>
                  )}
                </div>
              </div>
            ),
          },
          {
            title: "Pickup address",
            field: "pickupAddress",
            sorting: true,
            searchable: true,
            customSort: (a, b) => a.postcode.localeCompare(b.postcode),
            render: rowData => (
              <div data-label="Pickup address">{rowData.pickupAddress}</div>
            ),
          },
          {
            title: "Lesson type",
            field: "lessonType",
            sorting: true,
            searchable: false,
            render: rowData => (
              <div data-label="Lesson type">
                {lessonTypeToDisplayString(rowData.lessonType)}
              </div>
            ),
          },
          {
            title: "Last lesson",
            field: "lastLesson",
            sorting: true,
            defaultSort: "desc",
            searchable: false,
            type: "datetime",
            render: rowData => (
              <div data-label="Last lesson">
                {rowData.lastLesson && rowData.lastLesson <= new Date()
                  ? formatDistance(rowData.lastLesson, new Date(Date.now()), {
                      addSuffix: true,
                    })
                  : "n/a"}
              </div>
            ),
          },
        ]}
        actions={[
          {
            icon: () => <ArrowRight />,
            onClick: (event, data: LearnersTableItem | LearnersTableItem[]) =>
              onRowClick(event, data),
            tooltip: "View learner",
          },
        ]}
        options={{
          emptyRowsWhenPaging: false,
          actionsColumnIndex: -1,
          draggable: false,
          headerStyle: {
            whiteSpace: "nowrap",
          },
          paging: true,
          pageSize: 10,
          searchFieldAlignment: "left",
          searchFieldStyle: {
            marginLeft: theme.spacing(2),
          },
        }}
        localization={{
          header: { actions: "Action" },
          toolbar: { searchPlaceholder: "Search for learners" },
          body: { emptyDataSourceMessage: "No learners to display" },
        }}
        onRowClick={(event, data) => onRowClick(event, data)}
        components={{
          Pagination: props => (
            <TablePagination {...props} className={classes.pagination} />
          ),
          Toolbar: props => (
            <LearnersTableToolbar
              {...props}
              toggleActiveFilter={toggleActiveFilter}
              activeFilter={activeFilter}
            />
          ),
          Container: props => <Paper {...props} elevation={1} />,
          Row: props => <Row data-test="learners-table-row" {...props} />,
          Cell: props => <Cell {...props} className={classes.cell} />,
        }}
        data={learners}
      />
    </Box>
  );
};

export default LearnersTable;
